/*
  Shift modular · 360 turntable (sections/sr-turntable.liquid, Display: 360 turntable)
  Spec: Figma "Components · Turntable 360" (1040:2), from sections/dusk-l-wood-360.liquid.
  - Press and drag: every N px of horizontal drag = 1 frame, drag right = forward.
    Hover alone does nothing. No autoplay. Arrow keys / Home / End step frames.
    Two round buttons (rotate left / right) turn it 1/8 of a revolution per press,
    for anyone who can't drag.
    A sideways trackpad scroll also turns it; vertical scrolling is never captured.
  - Finish or size change: loads that set behind the loading cover (% + lime bar),
    then resets to frame 0000. Loads in 2 stages: every 4th frame first (the cover
    lifts and it turns in coarser steps), then the rest at low priority once the
    page has loaded, so the 360 doesn't compete with the hero for bandwidth.
  - Follows the product's variant: Dawn's variantChange (pubsub) or a document
    "sr:variant-change" event with detail { variantId } or { finish, size }.
  - Cursor badge on desktop with a mouse only; eased unless reduced motion is on.
  - A tap (press and release without moving) turns it 1/8, the same as the right button.
  - Touch hint "Drag to rotate" once the frames are in; it goes after the first drag,
    tap, arrow key or button press and stays gone for the visit (sessionStorage).
*/

(() => {
  'use strict';
  if (customElements.get('sr-turntable')) return;

  const withWidth = (url, width) =>
    /[?&]width=\d+/.test(url)
      ? url.replace(/([?&]width=)\d+/, `$1${width}`)
      : `${url}${url.indexOf('?') > -1 ? '&' : '?'}width=${width}`;

  const HINT_KEY = 'sr-tt-hint-done';
  const hintDone = () => {
    try {
      return sessionStorage.getItem(HINT_KEY) === '1';
    } catch (e) {
      return false;
    }
  };

  const safeQuery = (sel) => {
    try {
      return document.querySelector(sel);
    } catch (e) {
      return null;
    }
  };

  class SrTurntable extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;

      /* Optional: move into another element (e.g. the buy box media column) */
      const target = this.dataset.moveInto ? safeQuery(this.dataset.moveInto) : null;
      if (target && !target.contains(this) && !this._moved) {
        this._moved = true;
        target
          .querySelectorAll(`sr-turntable[data-section-id="${this.dataset.sectionId}"]`)
          .forEach((el) => el.remove());
        target.appendChild(this);
        return; /* connectedCallback runs again in the new place */
      }

      const dataEl = this.querySelector('script[data-sr-tt-data]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      this._ready = true;
      /* One signal for every listener, so a disconnect + reconnect never doubles them */
      this._ac = new AbortController();
      const signal = this._ac.signal;

      this.stage = this.querySelector('[data-sr-tt-stage]');
      this.img = this.querySelector('[data-sr-tt-frame]');
      this.cover = this.querySelector('[data-sr-tt-loading]');
      this.pct = this.querySelector('[data-sr-tt-pct]');
      this.fill = this.querySelector('[data-sr-tt-fill]');
      this.badge = this.querySelector('[data-sr-tt-badge]');
      this.hint = this.querySelector('[data-sr-tt-hint]');
      this.finishName = this.querySelector('[data-sr-tt-finish-name]');
      this.sizeName = this.querySelector('[data-sr-tt-size-name]');

      this.sens = Math.max(1, Number(this.dataset.sensitivity) || 10);
      this.finish = Number(this.dataset.finish) || 0;
      this.size = Number(this.dataset.size) || 0;
      this.frames = this.img && this.img.getAttribute('src') ? [this.img.getAttribute('src')] : [];
      this.index = 0;
      this.cache = new Map();
      this.token = this.token || 0; /* never reset: stale loads from before a reconnect must stay stale */
      this.started = false;
      this.wheelAcc = 0;
      this.badgeX = 0;
      this.badgeY = 0;

      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.wide = window.matchMedia('(min-width: 750px)');

      this.bindStage(signal);
      this.bindControls(signal);
      if (this.hasAttribute('data-follow-variant')) this.bindVariant(signal);

      this.onWide = () => {
        if (this.started) this.show(this.finish, this.size, true);
      };
      this.wide.addEventListener('change', this.onWide, { signal });

      this.onUnload = (e) => {
        if (this._moved && e.detail && e.detail.sectionId === this.dataset.sectionId) this.remove();
      };
      document.addEventListener('shopify:section:unload', this.onUnload, { signal });

      /* Start loading frames once the stage is near the viewport */
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (entries.some((en) => en.isIntersecting)) {
              this.io.disconnect();
              this.start();
            }
          },
          { rootMargin: '400px 0px' }
        );
        this.io.observe(this);
      } else {
        this.start();
      }
    }

    disconnectedCallback() {
      if (!this._ready) return;
      this._ready = false;
      this.token++;
      if (this.io) this.io.disconnect();
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
      if (this._ac) this._ac.abort();
      this.dragging = false;
      cancelAnimationFrame(this.badgeRaf);
      this.badgeRaf = 0;
    }

    start() {
      if (this.started) return;
      this.started = true;
      this.show(this.finish, this.size, true);
    }

    /* ---------- Frame sets ---------- */

    listFor(f, s) {
      const finish = this.data.finishes && this.data.finishes[f];
      if (!finish) return [];
      const lists = finish.frames || [];
      if (lists[s] && lists[s].length) return lists[s];
      return lists.find((l) => l && l.length) || [];
    }

    show(f, s, force) {
      if (!this.data.finishes || !this.data.finishes[f]) return;
      const changed = f !== this.finish || s !== this.size;
      this.finish = f;
      this.size = s;
      this.syncControls();
      if (!changed && !force) return;
      if (!this.started) return; /* loads when it scrolls into view */

      const width = this.wide.matches ? 1800 : 1200;
      const urls = this.listFor(f, s).map((u) => withWidth(u, width));
      /* New token first: any in-flight load for the previous pick is now stale */
      const token = ++this.token;
      if (!urls.length) {
        this.hideCover();
        return;
      }

      const key = `${f}|${s}|${width}`;
      const cached = this.cache.get(key);
      if (cached) {
        this.apply(cached.urls);
        return;
      }
      if (urls.length === 1) {
        this.cache.set(key, { urls, imgs: [] });
        this.apply(urls);
        return;
      }

      /* Stage 1: every 4th frame (and the last). Until a frame arrives, it shows
         the key frame before it, so dragging works with the full frame count. */
      const STEP = 4;
      const n = urls.length;
      const keys = urls.map((u, i) => i).filter((i) => i % STEP === 0 || i === n - 1);
      const rest = urls.map((u, i) => i).filter((i) => !keys.includes(i));
      const shown = urls.map((u, i) => urls[i - (i % STEP)]);
      const imgs = [];
      let done = 0;
      this.setProgress(0);
      if (this.cover) this.cover.hidden = false;
      if (this.stage) this.stage.setAttribute('aria-busy', 'true');
      this.syncRotate();

      const load = (i, onDone, low) => {
        const im = new Image();
        im.decoding = 'async';
        if (low) im.fetchPriority = 'low';
        im.onload = im.onerror = () => onDone(i);
        im.src = urls[i];
        imgs.push(im);
      };

      const fillRest = () => {
        if (token !== this.token) return;
        let left = rest.length;
        if (!left) {
          this.cache.set(key, { urls, imgs });
          return;
        }
        rest.forEach((i) =>
          load(i, (j) => {
            if (token !== this.token) return;
            shown[j] = urls[j];
            if (this.frames === shown && this.index === j) this.setIndex(j);
            if (--left === 0) this.cache.set(key, { urls, imgs });
          }, true)
        );
      };

      keys.forEach((i) =>
        load(i, () => {
          done++;
          if (token !== this.token) return;
          this.setProgress(done / keys.length);
          if (done < keys.length) return;
          this.apply(shown);
          /* Stage 2 once the page itself has finished loading */
          if (document.readyState === 'complete') setTimeout(fillRest, 0);
          else window.addEventListener('load', () => setTimeout(fillRest, 0), { once: true });
        })
      );
    }

    hideCover() {
      if (this.cover) this.cover.hidden = true;
      if (this.stage) this.stage.removeAttribute('aria-busy');
      this.syncRotate();
    }

    apply(urls) {
      /* The server-rendered <picture> source would override every frame set below */
      this.querySelectorAll('[data-sr-tt-init]').forEach((el) => el.remove());
      this.hideCover();
      this.frames = urls;
      if (this.img) this.img.hidden = false;
      this.setIndex(0);
      this.showHint();
    }

    /* ---------- Touch hint ---------- */

    showHint() {
      if (this.hint && this.frames.length > 1 && !hintDone()) this.hint.classList.add('is-on');
    }

    /* Called on every turn the visitor makes */
    dismissHint() {
      if (!this.hint || !this.hint.classList.contains('is-on')) return;
      this.hint.classList.remove('is-on');
      try {
        sessionStorage.setItem(HINT_KEY, '1');
      } catch (e) {
        /* private mode: it just comes back on the next page */
      }
    }

    setIndex(i) {
      const n = this.frames.length;
      if (!n || !this.img) return;
      this.index = ((i % n) + n) % n;
      const src = this.frames[this.index];
      if (this.img.getAttribute('src') !== src) this.img.setAttribute('src', src);
      this.stage.setAttribute('aria-valuemax', String(n - 1));
      this.stage.setAttribute('aria-valuenow', String(this.index));
      this.stage.setAttribute('aria-valuetext', `${Math.round((this.index * 360) / n)}°`);
      this.syncRotate();
    }

    /* Rotate buttons are disabled while a set loads, or when there is only 1 frame */
    syncRotate() {
      if (!this.rotateBtns) return;
      const off = !this.stage || this.stage.getAttribute('aria-busy') === 'true' || this.frames.length < 2;
      this.rotateBtns.forEach((b) => b.setAttribute('aria-disabled', off ? 'true' : 'false'));
    }

    setProgress(p) {
      const pct = Math.round(p * 100);
      if (this.pct) this.pct.textContent = `${pct}%`;
      if (this.fill) this.fill.style.transform = `scaleX(${pct / 100})`;
    }

    /* ---------- Drag, keys, wheel ---------- */

    bindStage(signal) {
      const st = this.stage;
      if (!st) return;
      const on = (type, fn, opts) => st.addEventListener(type, fn, { ...opts, signal });

      on('pointerdown', (e) => {
        if (e.button !== 0 || this.frames.length < 2) return;
        this.dragging = true;
        this.startX = e.clientX;
        this.startY = e.clientY;
        this.startT = e.timeStamp;
        this.startIndex = this.index;
        try {
          st.setPointerCapture(e.pointerId);
        } catch (err) {
          /* ignore */
        }
        st.classList.add('is-dragging');
        this.setBadge(true);
      });

      on('pointermove', (e) => {
        if (e.pointerType === 'mouse') this.moveBadge(e);
        if (!this.dragging) return;
        const steps = Math.trunc((e.clientX - this.startX) / this.sens);
        this.setIndex(this.startIndex + steps);
        if (steps) this.dismissHint();
      });

      const end = () => {
        if (!this.dragging) return;
        this.dragging = false;
        st.classList.remove('is-dragging');
        this.setBadge(true);
      };
      /* A tap: released within 500ms, less than 8px from where it started */
      on('pointerup', (e) => {
        if (
          this.dragging &&
          this.index === this.startIndex &&
          Math.abs(e.clientX - this.startX) < 8 &&
          Math.abs(e.clientY - this.startY) < 8 &&
          e.timeStamp - this.startT < 500
        ) {
          this.setIndex(this.index + Math.max(1, Math.round(this.frames.length / 8)));
          this.dismissHint();
        }
        end();
      });
      on('pointercancel', end);
      on('lostpointercapture', end);

      on('pointerenter', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.placeBadge(e, true);
        this.setBadge(true);
      });
      on('pointerleave', (e) => {
        if (e.pointerType !== 'mouse') return;
        this.setBadge(false);
      });

      on('keydown', (e) => {
        const n = this.frames.length;
        if (n < 2) return;
        const big = Math.max(1, Math.round(n / 8));
        const map = {
          ArrowRight: 1,
          ArrowUp: 1,
          ArrowLeft: -1,
          ArrowDown: -1,
          PageUp: big,
          PageDown: -big,
        };
        if (e.key in map) {
          e.preventDefault();
          this.setIndex(this.index + map[e.key]);
        } else if (e.key === 'Home') {
          e.preventDefault();
          this.setIndex(0);
        } else if (e.key === 'End') {
          e.preventDefault();
          this.setIndex(n - 1);
        } else {
          return;
        }
        this.dismissHint();
      });

      on(
        'wheel',
        (e) => {
          if (this.frames.length < 2) return;
          if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
          e.preventDefault();
          this.wheelAcc += e.deltaX;
          const steps = Math.trunc(this.wheelAcc / this.sens);
          if (steps) {
            this.wheelAcc -= steps * this.sens;
            this.setIndex(this.index + steps);
            this.dismissHint();
          }
        },
        { passive: false }
      );
    }

    /* ---------- Cursor badge ---------- */

    setBadge(on) {
      if (!this.badge) return;
      this.badge.classList.toggle('is-on', on);
      this.badge.textContent = this.dragging ? this.dataset.drag || '' : this.dataset.grab || '';
    }

    placeBadge(e, jump) {
      const r = this.stage.getBoundingClientRect();
      this.targetX = e.clientX - r.left;
      this.targetY = e.clientY - r.top;
      if (jump || this.reduced.matches) {
        this.badgeX = this.targetX;
        this.badgeY = this.targetY;
        this.drawBadge();
      }
    }

    moveBadge(e) {
      if (!this.badge) return;
      this.placeBadge(e, false);
      if (this.reduced.matches || this.badgeRaf) return;
      const step = () => {
        this.badgeX += (this.targetX - this.badgeX) * 0.15;
        this.badgeY += (this.targetY - this.badgeY) * 0.15;
        this.drawBadge();
        if (Math.abs(this.targetX - this.badgeX) > 0.5 || Math.abs(this.targetY - this.badgeY) > 0.5) {
          this.badgeRaf = requestAnimationFrame(step);
        } else {
          this.badgeRaf = 0;
        }
      };
      this.badgeRaf = requestAnimationFrame(step);
    }

    drawBadge() {
      this.badge.style.transform = `translate(${this.badgeX}px, ${this.badgeY}px)`;
    }

    /* ---------- Finish / size buttons ---------- */

    bindControls(signal) {
      this.rotateBtns = Array.from(this.querySelectorAll('[data-sr-tt-step]'));
      this.status = this.querySelector('[data-sr-tt-status]');
      this.rotateBtns.forEach((btn) =>
        btn.addEventListener(
          'click',
          () => {
            const n = this.frames.length;
            if (btn.getAttribute('aria-disabled') === 'true' || n < 2) return;
            /* 1/8 of a turn per press, the same as PageUp / PageDown */
            this.setIndex(this.index + Number(btn.dataset.srTtStep) * Math.max(1, Math.round(n / 8)));
            this.dismissHint();
            if (this.status) this.status.textContent = `${Math.round((this.index * 360) / n)}° of 360°`;
          },
          { signal }
        )
      );
      this.syncRotate();
      this.finishInputs = Array.from(this.querySelectorAll('input[data-sr-tt-finish]'));
      this.sizeInputs = Array.from(this.querySelectorAll('input[data-sr-tt-size]'));
      this.finishInputs.forEach((input) =>
        input.addEventListener('change', () => this.show(Number(input.value), this.size), { signal })
      );
      this.sizeInputs.forEach((input) =>
        input.addEventListener('change', () => this.show(this.finish, Number(input.value)), { signal })
      );
    }

    syncControls() {
      this.finishInputs.forEach((input) => {
        input.checked = Number(input.value) === this.finish;
      });
      this.sizeInputs.forEach((input) => {
        input.checked = Number(input.value) === this.size;
      });
      const f = this.data.finishes[this.finish];
      const s = this.data.sizes && this.data.sizes[this.size];
      if (this.finishName && f) this.finishName.textContent = f.name;
      if (this.sizeName && s) this.sizeName.textContent = s.label;
    }

    /* ---------- Variant ---------- */

    bindVariant(signal) {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event && event.data && event.data.variant;
          if (variant) this.fromVariantId(variant.id);
        });
      }
      this.onCustomVariant = (e) => {
        const d = e.detail || {};
        if (d.variantId) this.fromVariantId(d.variantId);
        else this.fromValues(d.finish, d.size);
      };
      document.addEventListener('sr:variant-change', this.onCustomVariant, { signal });
    }

    fromVariantId(id) {
      const v = this.data.variants && this.data.variants[String(id)];
      if (v) this.fromValues(v[0], v[1]);
    }

    fromValues(finish, size) {
      const fv = String(finish || '').trim().toLowerCase();
      const sv = String(size || '').trim().toLowerCase();
      let f = this.finish;
      let s = this.size;
      if (fv) {
        const i = this.data.finishes.findIndex((x) => x.match === fv);
        if (i > -1) f = i;
      }
      if (sv && this.data.sizes) {
        let i = this.data.sizes.findIndex((x) => x.match === sv);
        if (i < 0) i = this.data.sizes.findIndex((x) => x.short && sv.indexOf(x.short) === 0);
        if (i > -1) s = i;
      }
      this.show(f, s);
    }
  }

  customElements.define('sr-turntable', SrTurntable);
})();
